/* What the broker takes, and where it has to show up.
 *
 * A commission is not a rounding detail: on a small position it is most of the
 * first day's move, and a book that ignores it reports a gain that never
 * existed. It touches three things and has to be right in all three, or two of
 * them quietly disagree.
 *
 *   **The cost basis.** Money spent to acquire something is part of what it
 *   cost you — so the commission is spread across the shares it bought, and the
 *   average cost the book keeps is the price plus that share of the fee. This
 *   is also what a tax return wants: the cost of acquisition, not the fill
 *   price. It does mean the book's average differs slightly from the average
 *   the broker prints, which is why the screen says so rather than leaving
 *   somebody to find it.
 *
 *   **The cash.** What leaves the account is the consideration plus the fee.
 *   What arrives from a sale is the consideration less it.
 *
 *   **The realised result.** A sale nets the fee off what it made, so the
 *   figure that reaches the tax screen is what the trade actually produced.
 *
 * The fee itself is kept on the trade as its own number, not folded away, so a
 * year's commissions can be asked about later and none of this has to be
 * reverse-engineered from an average. */

/** The average cost once a buy commission is spread across the shares it
    bought. Zero shares has no average to speak of, and returns the price
    untouched rather than dividing by nothing. */
export function costWithFee(shares: number, price: number, fee = 0): number {
  const size = Math.abs(shares);
  if (!(size > 0) || !Number.isFinite(price)) return price;
  const paid = Number.isFinite(fee) ? Math.abs(fee) : 0;
  return price + paid / size;
}

/** What leaves the account to buy: the consideration and what the broker took.
    Returned positive — the caller decides the direction. */
export function buyCost(shares: number, price: number, fee = 0): number {
  const consideration = Math.abs(shares) * Math.abs(price);
  return consideration + (Number.isFinite(fee) ? Math.abs(fee) : 0);
}

/* What arrives from a sale: the consideration less what the broker took.
 *
 * It can come out negative, and is allowed to: a fee larger than the proceeds
 * is what closing a tiny position at some brokers actually does, and clamping
 * it at zero would report money arriving that did not. */
export function saleProceeds(shares: number, price: number, fee = 0): number {
  const consideration = Math.abs(shares) * Math.abs(price);
  return consideration - (Number.isFinite(fee) ? Math.abs(fee) : 0);
}

/** What a sale made, after the cost of getting out. The cost of getting in is
    already inside `avgCost`, so it is not subtracted twice. */
export function realisedAfterFee(
  shares: number, price: number, avgCost: number, fee = 0,
): number {
  const size = Math.abs(shares);
  return size * (price - avgCost) - (Number.isFinite(fee) ? Math.abs(fee) : 0);
}
